import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { priceDefaults, priceReport, type PriceInputs } from "@/lib/tools/calc";
import { NumberField, ToolPageShell, toolHead } from "@/components/site/tools/ToolPageShell";
import { PriceChart } from "@/components/site/tools/ToolCharts";

export const Route = createFileRoute("/tools/price-change-impact-calculator")({
  head: () => toolHead("price-change-impact-calculator"),
  component: PriceChangePage,
});

const PRESETS = [-3, 3, 6, 9];

function PriceChangePage() {
  const [i, setI] = useState<PriceInputs>(priceDefaults);
  const set = (k: keyof PriceInputs) => (v: number | null) => setI((p) => ({ ...p, [k]: v ?? 0 }));
  const setPrice = (v: number) => setI((p) => ({ ...p, price: Math.max(-9, Math.min(9, Math.round(v * 2) / 2)) }));
  return (
    <ToolPageShell
      slug="price-change-impact-calculator"
      report={priceReport(i)}
      inputs={i}
      chart={<PriceChart inputs={i} />}
      form={
        <div className="grid gap-5">
          <div className="grid gap-4 sm:grid-cols-2">
            <NumberField id="pc-rev" label="Annual revenue" prefix="$" value={i.revenue} onChange={set("revenue")} min={0} />
            <NumberField id="pc-gm" label="Gross margin" suffix="%" value={i.margin} onChange={(v) => setI((p) => ({ ...p, margin: Math.max(0.5, Math.min(95, v ?? 0.5)) }))} min={0.5} max={95} />
          </div>
          <div>
            <label htmlFor="pc-price" className="text-sm font-medium text-foreground">
              Price change: <span className="font-semibold text-midnight">{i.price > 0 ? "+" : ""}{i.price}%</span>
            </label>
            <input
              id="pc-price"
              type="range"
              min={-9}
              max={9}
              step={0.5}
              value={i.price}
              onChange={(e) => setPrice(Number(e.target.value))}
              aria-valuetext={`${i.price} percent`}
              className="mt-2 w-full accent-midnight focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-ring"
            />
            <div className="mt-3 flex flex-wrap gap-2" role="group" aria-label="Preset price moves">
              {PRESETS.map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => setPrice(p)}
                  aria-pressed={i.price === p}
                  className={`min-h-10 rounded-full border px-4 text-sm font-semibold transition focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring ${i.price === p ? "border-midnight bg-midnight text-frost" : "border-border bg-card text-midnight hover:border-steel"}`}
                >
                  {p > 0 ? "+" : ""}{p}%
                </button>
              ))}
            </div>
          </div>
          <NumberField id="pc-vol" label="Expected volume change" suffix="%" value={i.volume} onChange={set("volume")} min={-100} max={500} hint="Your own assumption. Negative if you expect to lose some volume." />
        </div>
      }
    />
  );
}
